import '@pages/OrderManagement/styles.css'

import { EllipsisOutlined, ExpandAltOutlined, LoadingOutlined, PictureFilled, ShrinkOutlined } from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import { CustomSearchBar } from '@components/StoreMonitoring/StoreDashboard/CustomSearchBar'
import { Filters } from '@components/StoreMonitoring/StoreDashboard/Filters'
import { OrderDrawer } from '@components/_SiteWide/OrderDisplay/OrderDrawer'
import { ProjectedDropToggle } from '@components/_SiteWide/TankDisplay/ProjectedDropToggle'
import { GraviButton, GraviGrid, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { Dropdown, Menu, Table, Tooltip } from 'antd'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getColumnDefs } from './columnDefs'
import { tankColumns } from './columnsVars'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'site-monitoring'

export function StoreMonitoringGrid({
  showingMap,
  setShowingMap,
  onStoreHover,
  canCreateOrder,
  rundownToggled,
  setToggle,
}) {
  const {
    filters,
    setFilters,
    loading,
    showModalWithStore,
    showModal,
    data,
    setFilteredStores,
    detailStoreIds,
    setDetailStoreIds,
    customSelectedStores,
    setCustomSelectedStores,
  } = useSiteMonitoringContext()
  const [orderPreviewStoreId, setOrderPreviewStoreId] = useState()
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false)
  const navigate = useNavigate()
  const { updateSetting, getSetting } = useUserSettings()

  const closeOrderDrawer = () => setOrderPreviewStoreId(undefined)
  const openDrawer = (storeId) => setOrderPreviewStoreId(storeId)
  const stores = data?.stores || []
  const gridAPIRef = useRef(null)

  useEffect(() => {
    if (gridAPIRef?.current) {
      gridAPIRef.current.redrawRows()
    }
  }, [rundownToggled])

  useEffect(() => {
    if (customSelectedStores?.length && gridAPIRef?.current) {
      gridAPIRef.current?.setFilterModel({
        store_number: { filterType: 'set', values: customSelectedStores?.map((s) => s.label) || null },
      })
    }
  }, [gridAPIRef?.current, data])
  const onFilterChanged = (event) => {
    const gridApi = event.api
    const newFilteredStores = []
    gridApi.forEachNodeAfterFilter((node) => {
      newFilteredStores.push(node.data)
    })
    setFilteredStores(newFilteredStores)
    const storeFilterModel = gridApi.getFilterModel('store_number')
    if (!storeFilterModel?.store_number?.values) {
      setCustomSelectedStores([])
    } else if (
      typeof event.afterFloatingFilter === 'boolean' &&
      typeof event.afterDataChange === 'boolean' &&
      newFilteredStores?.length !== customSelectedStores?.length
    ) {
      setCustomSelectedStores(newFilteredStores.map((store) => ({ value: store?.id, label: store?.store_number })))
    }
  }

  const setStoreList = (currentId) => {
    // get the list of store ids from the grid in the same order as the grid
    const storeList = []
    gridAPIRef.current?.forEachNodeAfterFilterAndSort((node) =>
      storeList.push({ id: node.data.id, number: node.data.store_number })
    )
    setDetailStoreIds(storeList)
    navigate(`/site_monitoring/${currentId}`)
  }
  const columnDefs = useMemo(
    () => getColumnDefs(openDrawer, showModalWithStore, canCreateOrder, rundownToggled, setStoreList),
    [rundownToggled]
  )
  const getRowId = useCallback((row) => row.data?.id, [])

  const agPropOverrides = useMemo(
    () => ({
      onFilterChanged,
      onRowDataChanged: onFilterChanged,
      maintainColumnOrder: true,
      getRowId,
      rowGroupPanelShow: 'never',
      frameworkComponents: {
        StoreRowExpand,
      },
    }),
    []
  )
  const onFirstDataRendered = (params) => {
    if (detailStoreIds?.length) {
      params.api.ensureNodeVisible((node) => node.data?.id === detailStoreIds, 'middle')
      setDetailStoreIds([])
    }
    getSetting('site-monitoring-custom-search').then((customSelectedStores) => {
      if (customSelectedStores?.length) {
        params.api.setFilterModel({
          store_number: { filterType: 'set', values: customSelectedStores?.map((s) => s.label) || null },
        })
      }
    })
  }

  return (
    <div className='store-view-grid-container' style={{ height: '100%' }}>
      <GraviGrid
        columnDefs={columnDefs}
        fullWidthSearchBar={true}
        controlBarProps={{
          title: 'Sites',
          actionButtons: (
            <ActionButtons
              rundownToggled={rundownToggled}
              setToggle={setToggle}
              setShowingMap={setShowingMap}
              showingMap={showingMap}
              gridAPIRef={gridAPIRef}
              hasBeenExpanded={hasBeenExpanded}
              setHasBeenExpanded={setHasBeenExpanded}
            />
          ),
          customFilterDrawer: <Filters />,
          filters,
          setFilters,
          hiddenFilterKeys: ['has_order', 'match_only_tank', 'view'],
          customSearchBar: <CustomSearchBar gridApiRef={gridAPIRef} />,
        }}
        externalRef={gridAPIRef}
        agPropOverrides={agPropOverrides}
        onFirstDataRendered={onFirstDataRendered}
        onRowGroupOpened={(params) => {
          if (params.node.expanded) {
            setHasBeenExpanded(true)
          } else {
            let x = false
            params.api.forEachNodeAfterFilter((node) => {
              if (node.expanded) x = true
            })
            setHasBeenExpanded(x)
          }
        }}
        detailRowAutoHeight
        detailCellRenderer='StoreRowExpand'
        masterDetail
        storageKey={STORAGE_KEY}
        rowData={stores}
        detailCellRendererParams={{
          refreshStrategy: 'everything',
          filters,
          onStoreHover,
          rundownToggled,
        }}
        loading={loading}
        onCellClicked={(params) => {
          const isCreateButton = params.event.target.innerHTML === 'Create Order'

          const isFlag = params.colDef.field === 'flags'
          const isStatusButton = params.event.target.classList.value === 'flex items-center'
          if (!isCreateButton && !isFlag && !isStatusButton) {
            params.node.setExpanded(!params.node.expanded)
          }
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />

      <OrderDrawer
        triggerModal={showModal}
        orderPreviewStoreId={orderPreviewStoreId}
        closeOrderDrawer={closeOrderDrawer}
      />
    </div>
  )
}
function ActionButtons({
  rundownToggled,
  setToggle,
  setShowingMap,
  showingMap,
  gridAPIRef,
  hasBeenExpanded,
  setHasBeenExpanded,
}) {
  const { isBackgroundRefreshing, setSortedInfo, sortedInfo } = useSiteMonitoringContext()
  const expandAll = () => {
    setHasBeenExpanded(true)
    gridAPIRef.current.forEachNode((node) => {
      node.setExpanded(true)
    })
  }
  const collapseAll = () => {
    setHasBeenExpanded(false)
    gridAPIRef.current.forEachNode((node) => {
      node.setExpanded(false)
    })
  }
  const handleMenuClick = (item) => {
    const { key } = item
    switch (key) {
      case '1':
        setSortedInfo((prev) => ({ columnKey: 'run_out_date', order: 'descend' }))
        break
      case '2':
        setSortedInfo((prev) => ({ columnKey: 'tanks_ids', order: 'ascend' }))
        break
      default:
        break
    }
  }
  const menu = (
    <Menu
      theme='light'
      onClick={handleMenuClick}
      style={{ minWidth: '150px' }}
      items={[
        {
          type: 'group',
          label: (
            <Horizontal className='border-bottom full-width-height' justifyContent='flex-start' alignItems='center'>
              <Texto category='h6' weight='bold' align='left' className='p-2'>
                Sort Tanks By:
              </Texto>
            </Horizontal>
          ),
          children: [
            {
              label: (
                <Texto className={classNames({ 'active-style': sortedInfo?.columnKey === 'run_out_date' })}>
                  Runout
                </Texto>
              ),
              key: '1',
            },
            {
              label: (
                <Texto className={classNames({ 'active-style': sortedInfo?.columnKey === 'tanks_ids' })}>Tank ID</Texto>
              ),
              key: '2',
            },
          ],
        },
      ]}
    />
  )
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
      {isBackgroundRefreshing && (
        <Texto weight='bold' style={{ whiteSpace: 'nowrap' }} appearance='secondary' className='border-right pr-3'>
          <LoadingOutlined className='mr-3' />
          Refreshing Data
        </Texto>
      )}
      <ProjectedDropToggle value={rundownToggled} onChange={() => setToggle(!rundownToggled)} />
      <Tooltip title={hasBeenExpanded ? 'Collapse All Tanks' : 'Expand All Tanks'}>
        {hasBeenExpanded ? (
          <GraviButton
            icon={<ShrinkOutlined />}
            color='primary'
            appearance='solid'
            className='mx-3'
            onClick={() => collapseAll()}
          />
        ) : (
          <GraviButton
            icon={<ExpandAltOutlined />}
            color='primary'
            appearance='outlined'
            className='mx-3'
            onClick={() => expandAll()}
          />
        )}
      </Tooltip>
      <Tooltip title={showingMap ? 'Hide Map' : 'Show Map'}>
        <GraviButton onClick={() => setShowingMap(!showingMap)} icon={<PictureFilled />} theme2={showingMap} />
      </Tooltip>
      <Dropdown overlay={menu}>
        <EllipsisOutlined rotate={-90} className='text-size-6' />
      </Dropdown>
    </div>
  )
}
function StoreRowExpand({ rundownToggled, filters, data }) {
  const { useSiteTanksQuery } = useSiteMonitoring()
  const { data: tankData, isPending } = useSiteTanksQuery(data.id, filters)
  const { setSortedInfo } = useSiteMonitoringContext()

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter)
  }
  return (
    <Table
      onChange={handleChange}
      className='store-monitoring-grid'
      loading={isPending}
      rowKey='soonest_runout'
      pagination={false}
      size='small'
      columns={tankColumns(rundownToggled, data.monitoring_strategy, data.tanks)}
      dataSource={tankData?.[data.id]}
    />
  )
}
